/**
 * Client-side Temporal polyfill installer — dynamic-imports `@js-temporal/polyfill`
 * only when the runtime doesn't already ship `Temporal` natively (Firefox 139+,
 * recent Chrome/Edge channels). Returns a Promise that resolves once
 * `globalThis.Temporal` is installed.
 *
 * Call once at app bootstrap before any module that touches `Temporal` at
 * runtime. On native-Temporal browsers the promise resolves on the microtask
 * queue — no chunk fetch, no blocking. Otherwise the polyfill is fetched from
 * its own chunk (`vendor-temporal` per vite manualChunks); subsequent calls
 * short-circuit on the cached promise.
 *
 * Server-side: a separate eager bootstrap lives at
 * `@gopass/server-common/utils/polyfill-temporal` and is imported synchronously
 * by every server entry point. tsx CJS rejects top-level await so Node entries
 * can't use this dynamic variant.
 */

export class TemporalPolyfillInstaller {
	private static installed: Promise<void> | null = null;

	static install(): Promise<void> {
		if (this.installed != null) {
			return this.installed;
		}

		if ((globalThis as any).Temporal != null) {
			this.installed = Promise.resolve();

			return this.installed;
		}

		this.installed = Promise.all([
			import('@js-temporal/polyfill'),
			import('@powerduck/common/extensions/temporal-extensions'),
			import('./extensions/inviton-temporal-extensions'),
		]).then(([
			polyfillMod,
			extensionsMod,
			invitonExtensionsMod,
		]) => {
			if ((globalThis as any).Temporal == null) {
				(globalThis as any).Temporal = polyfillMod.Temporal;
			}

			extensionsMod.applyTemporalExtensions();
			invitonExtensionsMod.applyInvitonTemporalExtensions();
		});

		return this.installed;
	}
}